import { useState } from "react";
import { motion } from "framer-motion";
import { Check, X, ChevronRight, User, Bot } from "lucide-react";

const fadeIn = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 }
};

function renderBold(text) {
  if (!text) return text;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function SectionRenderer({ section, index }) {
  switch (section.type) {
    case "paragraph":
      return (
        <motion.p {...fadeIn} transition={{ delay: index * 0.05 }} className="text-base leading-loose text-[#0A0A0A] mb-6 font-plex">
          {renderBold(section.content)}
        </motion.p>
      );
    case "bold":
      return (
        <motion.p {...fadeIn} className="text-lg font-semibold leading-relaxed text-[#0A0A0A] mb-6 font-plex border-l-2 border-[#FF4F00] pl-6">
          {renderBold(section.content)}
        </motion.p>
      );
    case "heading":
      return (
        <motion.h2 {...fadeIn} className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A] mt-12 mb-6 font-cabinet">
          {section.content}
        </motion.h2>
      );
    case "subheading":
      return (
        <motion.h3 {...fadeIn} className="text-xl font-medium tracking-tight text-[#0A0A0A] mt-8 mb-4 font-cabinet">
          {section.content}
        </motion.h3>
      );
    case "quote":
      return <QuoteBlock text={section.text} attribution={section.attribution} />;
    case "highlight":
      return <HighlightBox content={section.content} />;
    case "timeline":
      return <Timeline items={section.items} />;
    case "flipCards":
      return <FlipCardGrid cards={section.cards} />;
    case "quiz":
      return <Quiz question={section.question} options={section.options} correctIndex={section.correctIndex} explanation={section.explanation} />;
    case "table":
      return <ComparisonTable headers={section.headers} rows={section.rows} />;
    case "scorecard":
      return <Scorecard rounds={section.rounds} />;
    case "steps":
      return <ProcessSteps items={section.items} />;
    case "levels":
      return <ProficiencyLevels items={section.items} />;
    case "hierarchy":
      return <Hierarchy items={section.items} />;
    case "cards":
      return <InfoCards items={section.items} variant={section.variant} />;
    case "list":
      return <BulletList items={section.items} />;
    default:
      return null;
  }
}

function QuoteBlock({ text, attribution }) {
  return (
    <motion.blockquote {...fadeIn} className="my-10 border-l-2 border-[#FF4F00] pl-6 md:pl-8">
      <p className="text-lg md:text-xl leading-relaxed text-[#0A0A0A] font-cabinet font-medium italic">
        "{text}"
      </p>
      {attribution && (
        <cite className="block mt-3 text-sm text-[#666666] font-mono-plex not-italic">
          {attribution}
        </cite>
      )}
    </motion.blockquote>
  );
}

function HighlightBox({ content }) {
  return (
    <motion.div {...fadeIn} className="my-8 bg-[#F0F0F0] border border-[#E5E5E5] p-6 md:p-8">
      <p className="text-base leading-relaxed text-[#0A0A0A] font-plex">{renderBold(content)}</p>
    </motion.div>
  );
}

function Timeline({ items }) {
  return (
    <div className="my-10" data-testid="chapter-timeline">
      {items.map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.08 }}
          className="flex gap-4 md:gap-5"
        >
          <div className="flex flex-col items-center shrink-0 w-6">
            <div className={`w-3 h-3 rounded-full border-2 border-[#FF4F00] mt-1 ${i === items.length - 1 ? "bg-[#FF4F00]" : "bg-[#F9F9F8]"}`} />
            {i < items.length - 1 && <div className="w-[2px] flex-1 bg-[#E5E5E5]" />}
          </div>
          <div className="pb-8 min-w-0 flex-1">
            <span className="font-mono-plex text-xs tracking-[0.15em] uppercase text-[#FF4F00] font-bold">
              {item.year}
            </span>
            <h4 className="font-cabinet font-bold text-base mt-1 text-[#0A0A0A]">{item.title}</h4>
            <p className="text-sm text-[#666666] mt-1 leading-relaxed font-plex break-words">{item.desc}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function FlipCardGrid({ cards }) {
  const [flipped, setFlipped] = useState({});
  const toggle = (i) => setFlipped((prev) => ({ ...prev, [i]: !prev[i] }));

  return (
    <div className="my-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="flip-card-grid">
      {cards.map((card, i) => (
        <div
          key={i}
          className="flip-card h-[220px] md:h-[240px]"
          onClick={() => toggle(i)}
          data-testid={`flip-card-${i}`}
        >
          <div className={`flip-card-inner ${flipped[i] ? "flipped" : ""}`}>
            <div className="flip-card-front border border-[#E5E5E5] bg-white p-6 flex flex-col justify-center">
              <h4 className="font-cabinet font-bold text-lg text-[#0A0A0A]">{card.front}</h4>
              <p className="text-sm text-[#FF4F00] mt-2 font-mono-plex">{card.frontSub}</p>
            </div>
            <div className="flip-card-back border border-[#FF4F00] bg-white p-6 flex flex-col justify-center overflow-y-auto">
              <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-[#FF4F00] mb-2">Reality</span>
              <p className="text-sm leading-relaxed text-[#0A0A0A] font-plex">{card.back}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Quiz({ question, options, correctIndex, explanation }) {
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);

  const handleSelect = (i) => {
    if (revealed) return;
    setSelected(i);
    setRevealed(true);
  };

  return (
    <div className="my-10 border border-[#E5E5E5] bg-white p-6 md:p-8" data-testid="chapter-quiz">
      <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">Quick Check</span>
      <p className="font-cabinet font-medium text-lg mt-3 mb-6 text-[#0A0A0A]">{question}</p>
      <div className="space-y-3">
        {options.map((opt, i) => {
          let cls = "quiz-option";
          if (revealed && i === correctIndex) cls += " correct";
          else if (revealed && i === selected && i !== correctIndex) cls += " incorrect";
          else if (selected === i) cls += " selected";
          return (
            <div
              key={i}
              className={cls}
              onClick={() => handleSelect(i)}
              data-testid={`quiz-option-${i}`}
            >
              <span className="text-sm font-plex">{opt}</span>
            </div>
          );
        })}
      </div>
      {revealed && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 bg-[#F0F0F0] border border-[#E5E5E5]"
        >
          <div className="flex items-center gap-2 mb-2">
            {selected === correctIndex ? (
              <Check size={16} className="text-green-600" />
            ) : (
              <X size={16} className="text-red-600" />
            )}
            <span className="font-bold text-sm">
              {selected === correctIndex ? "Exactly right." : "Not quite."}
            </span>
          </div>
          <p className="text-sm text-[#666666] leading-relaxed font-plex">{explanation}</p>
        </motion.div>
      )}
    </div>
  );
}

function ComparisonTable({ headers, rows }) {
  return (
    <div className="my-10 overflow-x-auto" data-testid="comparison-table">
      <table className="w-full border border-[#E5E5E5]">
        <thead>
          <tr className="bg-[#0A0A0A]">
            {headers.map((h, i) => (
              <th key={i} className="px-4 py-3 text-left text-sm font-bold text-white font-cabinet">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-[#E5E5E5] hover:bg-[#F0F0F0] transition-colors">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 text-sm text-[#0A0A0A] font-plex leading-relaxed">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Scorecard({ rounds }) {
  return (
    <div className="my-10 space-y-4" data-testid="chapter-scorecard">
      {rounds.map((r, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.06 }}
          className="scorecard-round p-4 md:p-6"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono-plex text-xs tracking-[0.15em] uppercase text-[#FF4F00] font-bold">
              Round {String(r.round).padStart(2, "0")}
            </span>
            <span className={`text-xs font-bold font-mono-plex px-2 py-1 ${
              r.winner === "human" ? "bg-[#0A0A0A] text-white" : r.winner === "ai" ? "bg-[#FF4F00] text-white" : "bg-[#E5E5E5] text-[#666666]"
            }`}>
              {r.winner === "human" ? "HUMAN" : r.winner === "ai" ? "AI" : "TIE"}
            </span>
          </div>
          <h4 className="font-cabinet font-bold text-base mb-3 text-[#0A0A0A]">{r.title}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex gap-2">
              <User size={14} className="mt-0.5 text-[#666666] shrink-0" />
              <p className="text-sm text-[#666666] font-plex leading-relaxed">{r.human}</p>
            </div>
            <div className="flex gap-2">
              <Bot size={14} className="mt-0.5 text-[#FF4F00] shrink-0" />
              <p className="text-sm text-[#666666] font-plex leading-relaxed">{r.ai}</p>
            </div>
          </div>
        </motion.div>
      ))}
      <div className="text-center py-6 border border-[#E5E5E5] bg-white">
        <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">Final Score</span>
        <div className="flex items-center justify-center gap-8 mt-3">
          <div>
            <span className="font-cabinet text-3xl font-bold text-[#0A0A0A]">5</span>
            <span className="block text-xs text-[#666666] font-mono-plex mt-1">Human</span>
          </div>
          <span className="text-2xl text-[#E5E5E5]">:</span>
          <div>
            <span className="font-cabinet text-3xl font-bold text-[#FF4F00]">5</span>
            <span className="block text-xs text-[#666666] font-mono-plex mt-1">AI</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProcessSteps({ items }) {
  return (
    <div className="my-10 space-y-6" data-testid="process-steps">
      {items.map((step, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.08 }}
          className="flex gap-4 md:gap-6"
        >
          <div className="shrink-0 w-10 h-10 bg-[#0A0A0A] flex items-center justify-center">
            <span className="font-mono-plex text-xs font-bold text-white">{step.number}</span>
          </div>
          <div className="flex-1 pb-6 border-b border-[#E5E5E5] last:border-0">
            <h4 className="font-cabinet font-bold text-base text-[#0A0A0A]">{step.title}</h4>
            <p className="text-sm text-[#666666] mt-2 leading-relaxed font-plex">{step.desc}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function ProficiencyLevels({ items }) {
  return (
    <div className="my-10 space-y-3" data-testid="proficiency-levels">
      {items.map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: i * 0.08 }}
          className="flex items-start gap-4 p-4 border border-[#E5E5E5] bg-white hover:border-[#0A0A0A] transition-colors"
        >
          <div className="shrink-0 w-8 h-8 bg-[#FF4F00] flex items-center justify-center">
            <span className="font-mono-plex text-xs font-bold text-white">{String(item.level).padStart(2, "0")}</span>
          </div>
          <div>
            <h4 className="font-cabinet font-bold text-base text-[#0A0A0A]">{item.title}</h4>
            <p className="text-sm text-[#666666] mt-1 leading-relaxed font-plex">{item.desc}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function Hierarchy({ items }) {
  return (
    <div className="my-10 space-y-0" data-testid="hierarchy-diagram">
      {items.map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.12 }}
          className={`p-5 md:p-6 border border-[#E5E5E5] ${item.isHighlighted ? "bg-[#FF4F00] border-[#FF4F00]" : "bg-white"}`}
          style={{ marginLeft: `${i * 16}px` }}
        >
          <span className={`font-mono-plex text-[10px] tracking-[0.2em] uppercase ${item.isHighlighted ? "text-white/70" : "text-[#666666]"}`}>
            {item.subtitle}
          </span>
          <h4 className={`font-cabinet font-bold text-lg mt-1 ${item.isHighlighted ? "text-white" : "text-[#0A0A0A]"}`}>
            {item.title}
          </h4>
          <p className={`text-sm mt-2 leading-relaxed font-plex ${item.isHighlighted ? "text-white/90" : "text-[#666666]"}`}>
            {item.desc}
          </p>
        </motion.div>
      ))}
    </div>
  );
}

function InfoCards({ items, variant }) {
  return (
    <div className="my-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="info-cards">
      {items.map((card, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.06 }}
          className={`card-hover p-5 bg-white ${
            variant === "warning" ? "border-l-2 border-l-[#D32F2F]" :
            variant === "status" && card.tag?.includes("not") ? "border-l-2 border-l-[#D32F2F]" :
            variant === "status" ? "border-l-2 border-l-[#0A0A0A]" : ""
          }`}
        >
          {card.tag && (
            <span className={`font-mono-plex text-[10px] tracking-[0.15em] uppercase font-bold ${
              card.tag?.toLowerCase().includes("not") || card.tag?.toLowerCase().includes("critical") ? "text-[#D32F2F]" : "text-[#666666]"
            }`}>
              {card.tag}
            </span>
          )}
          <h4 className="font-cabinet font-bold text-base text-[#0A0A0A] mt-1">{card.title}</h4>
          <p className="text-sm text-[#666666] mt-2 leading-relaxed font-plex">{card.desc}</p>
        </motion.div>
      ))}
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="my-6 space-y-3">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <ChevronRight size={14} className="mt-1.5 text-[#FF4F00] shrink-0" />
          <span className="text-sm text-[#0A0A0A] leading-relaxed font-plex">{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function ChapterContent({ chapter }) {
  if (!chapter) return null;

  return (
    <article data-testid={`chapter-${chapter.id}-content`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-12"
      >
        <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#FF4F00] font-bold">
          Chapter {chapter.number}
        </span>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mt-3 font-cabinet text-[#0A0A0A] leading-tight">
          {chapter.title}
        </h1>
        <p className="text-lg text-[#666666] mt-4 font-plex leading-relaxed">
          {chapter.subtitle}
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-[2px] bg-[#FF4F00]" />
            <span className="font-mono-plex text-[10px] tracking-[0.12em] uppercase text-[#666666]">Thought by</span>
            <span className="font-cabinet font-semibold text-sm text-[#0A0A0A]">Shadab Rashid</span>
          </div>
          <span className="hidden sm:inline text-[#E5E5E5]">|</span>
          <div className="flex items-center gap-3 sm:gap-2">
            <span className="sm:hidden w-10 h-[2px] bg-[#E5E5E5]" />
            <span className="font-mono-plex text-[10px] tracking-[0.12em] uppercase text-[#666666]">Curated by</span>
            <span className="font-cabinet font-medium text-sm text-[#666666]">Shifa Shaikh</span>
          </div>
        </div>
      </motion.div>

      {chapter.sections.map((section, i) => (
        <SectionRenderer key={i} section={section} index={i} />
      ))}
    </article>
  );
}
